import { useEffect, useRef, useState, useCallback } from "react";
import { useParams, useNavigate, useSearchParams } from "react-router-dom";
import { api } from "@/lib/api";
import { toast } from "sonner";
import { useAuth } from "@/context/AuthContext";
import HlsVideo from "@/components/HlsVideo";
import {
  Play, Pause, Volume2, VolumeX, Maximize, Settings, ArrowLeft,
  SkipForward, Rewind, FastForward, Loader2, Check, Subtitles, Cast, Crown,
} from "lucide-react";
import {
  DropdownMenu, DropdownMenuContent, DropdownMenuTrigger,
  DropdownMenuLabel, DropdownMenuItem, DropdownMenuSeparator,
} from "@/components/ui/dropdown-menu";

const SPEEDS = [0.5, 0.75, 1, 1.25, 1.5, 2];
const INTRO_END = 85;
const PREVIEW_LIMIT = 300; // 5-minute preview for non-subscribers (movies)

function fmt(t) {
  if (!t || isNaN(t)) return "0:00";
  const m = Math.floor(t / 60);
  const s = Math.floor(t % 60);
  return `${m}:${s.toString().padStart(2, "0")}`;
}

export default function Watch() {
  const { id } = useParams();
  const [sp] = useSearchParams();
  const epId = sp.get("ep");
  const navigate = useNavigate();
  const { user } = useAuth();

  const [access, setAccess] = useState(null); // null=checking, true=entitled, false=not entitled

  const videoRef = useRef(null);
  const hlsRef = useRef(null);
  const containerRef = useRef(null);
  const hideTimer = useRef(null);
  const saveTimer = useRef(null);

  const [content, setContent] = useState(null);
  const [src, setSrc] = useState(null);
  const [currentEp, setCurrentEp] = useState(null);
  const [playing, setPlaying] = useState(false);
  const [time, setTime] = useState(0);
  const [duration, setDuration] = useState(0);
  const [buffered, setBuffered] = useState(0);
  const [volume, setVolume] = useState(1);
  const [muted, setMuted] = useState(false);
  const [showControls, setShowControls] = useState(true);
  const [speed, setSpeed] = useState(1);
  const [levels, setLevels] = useState([]);
  const [currentLevel, setCurrentLevel] = useState(-1);
  const [audioTracks, setAudioTracks] = useState([]);
  const [currentAudio, setCurrentAudio] = useState(0);
  const [subTracks, setSubTracks] = useState([]);
  const [currentSub, setCurrentSub] = useState(-1);
  const [resumeAt, setResumeAt] = useState(0);
  const [adActive, setAdActive] = useState(false);
  const [adInit, setAdInit] = useState(false);
  const [adCountdown, setAdCountdown] = useState(5);
  const [wmPos, setWmPos] = useState({ top: "12%", left: "8%" });
  const [locked, setLocked] = useState(false);
  const [adData, setAdData] = useState(null);

  useEffect(() => {
    window.scrollTo(0, 0);
    api.get(`/content/${id}`).then((r) => {
      const c = r.data;
      setContent(c);
      let ep = null;
      let url = c.video_url;
      if (c.type === "series" && c.episodes?.length) {
        ep = epId ? c.episodes.find((e) => e.id === epId) : c.episodes[0];
        if (ep) url = ep.video_url;
      }
      setCurrentEp(ep);
      setSrc(url);
    });
    api.get(`/progress/${id}`).then((r) => {
      if (r.data?.position && (!r.data.duration || r.data.position / r.data.duration < 0.95)) {
        setResumeAt(r.data.position);
      }
    }).catch(() => {});
  }, [id, epId]);

  // Fetch admin-managed pre-roll ad and decide whether to show it
  useEffect(() => {
    if (adInit || !content) return;
    setAdInit(true);
    api.get("/ads", { params: { placement: "preroll", content_id: id } })
      .then((r) => {
        const ad = (r.data || []).find((a) => a.active && (a.video_url || a.image_url));
        if (ad) { setAdData(ad); setAdActive(true); }
      })
      .catch(() => {});
  }, [content, adInit, id]);

  // Ad skip countdown
  useEffect(() => {
    if (!adActive || adCountdown <= 0) return;
    const t = setTimeout(() => setAdCountdown((c) => c - 1), 1000);
    return () => clearTimeout(t);
  }, [adActive, adCountdown]);

  const endAd = () => {
    setAdActive(false);
    const v = videoRef.current;
    if (v) v.play().catch(() => {});
  };

  // Verify entitlement (and confirm Stripe payment if returning from checkout)
  useEffect(() => {
    let cancelled = false;
    const sessionId = sp.get("session_id");
    (async () => {
      if (sessionId) {
        try { await api.get(`/checkout/status/${sessionId}`); }
        catch (e) { console.error("Checkout status confirmation failed:", e); }
      }
      try {
        const { data } = await api.get(`/access/${id}`, { params: epId ? { episode_id: epId } : {} });
        if (!cancelled) setAccess(!!data.access);
      } catch {
        if (!cancelled) setAccess(false);
      }
    })();
    return () => { cancelled = true; };
  }, [id, epId]); // eslint-disable-line

  useEffect(() => {
    if (access === false) {
      toast.error("Rent or buy this title to start watching");
      navigate(`/title/${id}`);
    }
  }, [access]); // eslint-disable-line

  // Anti-piracy deterrent: roaming watermark with the viewer's identity
  useEffect(() => {
    const i = setInterval(() => {
      setWmPos({ top: `${10 + Math.random() * 72}%`, left: `${5 + Math.random() * 68}%` });
    }, 4000);
    return () => clearInterval(i);
  }, []);

  // Pause playback when tab/window is hidden (deters background screen capture)
  useEffect(() => {
    const onVis = () => { if (document.hidden && videoRef.current) videoRef.current.pause(); };
    document.addEventListener("visibilitychange", onVis);
    return () => document.removeEventListener("visibilitychange", onVis);
  }, []);

  const onReady = useCallback((hls) => {
    hlsRef.current = hls;
    const lv = hls.levels.map((l, i) => ({ i, height: l.height, name: l.height ? `${l.height}p` : `Level ${i}` }));
    setLevels(lv);
    setAudioTracks((hls.audioTracks || []).map((a, i) => ({ i, name: a.name || a.lang || `Audio ${i + 1}` })));
    setSubTracks((hls.subtitleTracks || []).map((s, i) => ({ i, name: s.name || s.lang || `Sub ${i + 1}` })));
    hls.subtitleTrack = -1;
  }, []);

  const saveProgress = useCallback(() => {
    const v = videoRef.current;
    if (v && v.duration && v.currentTime > 0) {
      api.post("/progress", {
        content_id: id, position: v.currentTime, duration: v.duration,
        episode_id: currentEp?.id || null,
      }).catch(() => {});
    }
  }, [id, currentEp]);

  useEffect(() => {
    saveTimer.current = setInterval(saveProgress, 10000);
    return () => { clearInterval(saveTimer.current); saveProgress(); };
  }, [saveProgress]);

  const togglePlay = () => {
    const v = videoRef.current;
    if (!v) return;
    if (v.paused) v.play(); else v.pause();
  };

  const onTimeUpdate = () => {
    const v = videoRef.current;
    setTime(v.currentTime);
    if (v.buffered.length) setBuffered(v.buffered.end(v.buffered.length - 1));
  };

  const onLoaded = () => {
    const v = videoRef.current;
    setDuration(v.duration);
    if (resumeAt && resumeAt < v.duration) v.currentTime = resumeAt;
  };

  const seek = (e) => {
    const v = videoRef.current;
    v.currentTime = parseFloat(e.target.value);
    setTime(v.currentTime);
  };

  const skip = (s) => { videoRef.current.currentTime += s; };

  const changeSpeed = (s) => { videoRef.current.playbackRate = s; setSpeed(s); };

  const changeLevel = (i) => { if (hlsRef.current) hlsRef.current.currentLevel = i; setCurrentLevel(i); };
  const changeAudio = (i) => { if (hlsRef.current) hlsRef.current.audioTrack = i; setCurrentAudio(i); };
  const changeSub = (i) => { if (hlsRef.current) hlsRef.current.subtitleTrack = i; setCurrentSub(i); };

  const toggleMute = () => { const v = videoRef.current; v.muted = !v.muted; setMuted(v.muted); };
  const changeVolume = (e) => { const val = parseFloat(e.target.value); videoRef.current.volume = val; setVolume(val); setMuted(val === 0); };

  const toggleFullscreen = () => {
    if (!document.fullscreenElement) containerRef.current.requestFullscreen?.();
    else document.exitFullscreen?.();
  };

  const nextEpisode = () => {
    if (content?.type === "series" && content.episodes) {
      const idx = content.episodes.findIndex((e) => e.id === currentEp?.id);
      const next = content.episodes[idx + 1];
      if (next) navigate(`/watch/${id}?ep=${next.id}`);
    }
  };

  const hasNext = content?.type === "series" && content.episodes &&
    content.episodes.findIndex((e) => e.id === currentEp?.id) < content.episodes.length - 1;

  const showAndScheduleHide = () => {
    setShowControls(true);
    clearTimeout(hideTimer.current);
    hideTimer.current = setTimeout(() => { if (!videoRef.current?.paused) setShowControls(false); }, 3000);
  };

  const onEnded = () => {
    saveProgress();
    if (hasNext) nextEpisode();
  };

  if (!content || !src || access !== true) {
    return <div className="min-h-screen flex items-center justify-center bg-black"><Loader2 className="w-8 h-8 text-brand animate-spin" /></div>;
  }

  const showPaywall = false;

  return (
    <div
      ref={containerRef}
      data-testid="video-player"
      className="fixed inset-0 z-[100] bg-black flex items-center justify-center select-none"
      onMouseMove={showAndScheduleHide}
      onContextMenu={(e) => e.preventDefault()}
      onClick={(e) => { if (e.target.tagName === "VIDEO" && !adActive) togglePlay(); }}
    >
      <HlsVideo
        ref={videoRef}
        src={src}
        autoPlay={!adActive}
        onReady={onReady}
        controlsList="nodownload noremoteplayback noplaybackrate"
        disablePictureInPicture
        onPlay={() => setPlaying(true)}
        onPause={() => setPlaying(false)}
        onTimeUpdate={onTimeUpdate}
        onLoadedMetadata={onLoaded}
        onEnded={onEnded}
        className="w-full h-full object-contain"
      />

      {/* Roaming identity watermark (anti-piracy deterrent) */}
      {user && !adActive && (
        <div
          data-testid="player-watermark"
          className="absolute z-[105] pointer-events-none select-none text-white/20 text-[11px] font-mono tracking-wide"
          style={wmPos}
        >
          {user.email} · Mandalay+
        </div>
      )}

      {/* Pre-roll advertisement (admin-managed) */}
      {adActive && adData && !showPaywall && (
        <div data-testid="ad-overlay" className="absolute inset-0 z-[110] bg-black flex items-center justify-center" onContextMenu={(e) => e.preventDefault()}>
          {adData.video_url ? (
            <video src={adData.video_url} autoPlay onEnded={endAd} className="w-full h-full object-contain" />
          ) : (
            <img src={adData.image_url} alt={adData.name} className="w-full h-full object-contain" />
          )}
          <div className="absolute top-4 left-4 bg-black/70 px-3 py-1 rounded-sm text-xs uppercase tracking-widest text-gold">Advertisement</div>
          <div className="absolute bottom-6 right-6 flex items-center gap-3">
            {adData.link_url && (
              <a data-testid="ad-link-btn" href={adData.link_url} target="_blank" rel="noreferrer" className="bg-gold text-ink px-4 py-2 rounded-sm font-medium hover:bg-gold-bright transition-colors">Learn more</a>
            )}
            {adCountdown > 0 ? (
              <span className="bg-black/70 px-4 py-2 rounded-sm text-sm">Skip in {adCountdown}s</span>
            ) : (
              <button data-testid="skip-ad-btn" onClick={endAd} className="bg-white text-black px-5 py-2 rounded-sm font-medium flex items-center gap-2 hover:bg-white/90 transition-colors">
                Skip Ad <SkipForward className="w-4 h-4" strokeWidth={1.5} />
              </button>
            )}
          </div>
        </div>
      )}


      {/* Trial paywall (2 free episodes / 5-min movie preview) */}
      {showPaywall && (
        <div data-testid="paywall-overlay" className="absolute inset-0 z-[115] bg-black/92 flex flex-col items-center justify-center text-center px-6">
          <Crown className="w-12 h-12 text-gold mb-4" />
          <h2 className="font-display text-2xl sm:text-3xl font-black mb-2">
            {content.type === "series" ? "Free trial: first 2 episodes" : "Your 5-minute preview has ended"}
          </h2>
          <p className="text-white/70 max-w-md mb-6">
            {content.type === "series"
              ? "You've watched the free trial episodes. Subscribe to unlock every episode in HD."
              : "Subscribe to keep watching the full movie in stunning quality."}
          </p>
          <div className="flex gap-3">
            <button data-testid="paywall-subscribe" onClick={() => navigate("/browse")} className="bg-gold text-black font-semibold px-6 py-2.5 rounded-md hover:bg-gold/90 transition-colors">View Plans</button>
            <button data-testid="paywall-back" onClick={() => navigate(`/title/${id}`)} className="bg-white/10 hover:bg-white/20 px-6 py-2.5 rounded-md transition-colors">Back</button>
          </div>
        </div>
      )}

      {/* Top bar */}
      <div className={`absolute top-0 left-0 right-0 p-4 sm:p-6 bg-gradient-to-b from-black/80 to-transparent transition-opacity duration-300 ${showControls ? "opacity-100" : "opacity-0"}`}>
        <button data-testid="player-back-btn" onClick={() => { saveProgress(); navigate(`/title/${id}`); }} className="flex items-center gap-2 text-white hover:text-gold transition-colors">
          <ArrowLeft className="w-6 h-6" />
          <span className="font-display font-semibold">{content.title}{currentEp ? ` — ${currentEp.title}` : ""}</span>
        </button>
      </div>

      {/* Skip intro */}
      {time < INTRO_END && time > 3 && (
        <button
          data-testid="skip-intro-btn"
          onClick={() => { videoRef.current.currentTime = INTRO_END; }}
          className={`absolute bottom-28 right-6 bg-white/90 text-black font-semibold px-5 py-2 rounded-md hover:bg-white transition-all ${showControls ? "opacity-100" : "opacity-0"}`}
        >
          Skip Intro
        </button>
      )}

      {/* Center play */}
      {!playing && (
        <button onClick={togglePlay} className="absolute z-10 w-20 h-20 rounded-full bg-black/50 backdrop-blur flex items-center justify-center hover:bg-black/70 transition-colors">
          <Play className="w-10 h-10 fill-white" />
        </button>
      )}

      {/* Bottom controls */}
      <div className={`absolute bottom-0 left-0 right-0 px-4 sm:px-6 pb-4 pt-16 bg-gradient-to-t from-black/90 to-transparent transition-opacity duration-300 ${showControls ? "opacity-100" : "opacity-0 pointer-events-none"}`}>
        <div className="relative mb-3">
          <input
            data-testid="player-seek"
            type="range" min="0" max={duration || 0} value={time} step="0.1"
            onChange={seek}
            className="gs-range w-full"
            style={{ background: `linear-gradient(to right, #E50914 ${(time / (duration || 1)) * 100}%, rgba(255,255,255,0.15) ${(time / (duration || 1)) * 100}%)` }}
          />
        </div>
        <div className="flex items-center justify-between gap-2">
          <div className="flex items-center gap-3 sm:gap-4">
            <button data-testid="player-playpause" onClick={togglePlay}>{playing ? <Pause className="w-6 h-6 fill-white" /> : <Play className="w-6 h-6 fill-white" />}</button>
            <button data-testid="player-rewind" onClick={() => skip(-10)}><Rewind className="w-5 h-5" /></button>
            <button data-testid="player-forward" onClick={() => skip(10)}><FastForward className="w-5 h-5" /></button>
            {hasNext && <button data-testid="player-next" onClick={nextEpisode} title="Next episode"><SkipForward className="w-5 h-5" /></button>}
            <div className="hidden sm:flex items-center gap-2 group">
              <button onClick={toggleMute}>{muted || volume === 0 ? <VolumeX className="w-5 h-5" /> : <Volume2 className="w-5 h-5" />}</button>
              <input type="range" min="0" max="1" step="0.05" value={muted ? 0 : volume} onChange={changeVolume} className="gs-range w-20" />
            </div>
            <span className="text-sm text-white/80 tabular-nums">{fmt(time)} / {fmt(duration)}</span>
          </div>

          <div className="flex items-center gap-3 sm:gap-4">
            <button data-testid="player-cast" title="Chromecast / AirPlay" onClick={() => alert("Casting requires a Chromecast/AirPlay device on your network.")}><Cast className="w-5 h-5" /></button>
            {subTracks.length > 0 && (
              <DropdownMenu>
                <DropdownMenuTrigger data-testid="player-subtitles"><Subtitles className="w-5 h-5" /></DropdownMenuTrigger>
                <DropdownMenuContent className="bg-surface border-white/10 text-white">
                  <DropdownMenuLabel>Subtitles</DropdownMenuLabel>
                  <DropdownMenuItem onClick={() => changeSub(-1)} className="focus:bg-white/10">{currentSub === -1 && <Check className="w-4 h-4 mr-2" />} Off</DropdownMenuItem>
                  {subTracks.map((s) => (
                    <DropdownMenuItem key={s.i} onClick={() => changeSub(s.i)} className="focus:bg-white/10">{currentSub === s.i && <Check className="w-4 h-4 mr-2" />} {s.name}</DropdownMenuItem>
                  ))}
                </DropdownMenuContent>
              </DropdownMenu>
            )}
            <DropdownMenu>
              <DropdownMenuTrigger data-testid="player-settings"><Settings className="w-5 h-5" /></DropdownMenuTrigger>
              <DropdownMenuContent align="end" className="bg-surface border-white/10 text-white w-56 max-h-96 overflow-y-auto">
                <DropdownMenuLabel>Playback Speed</DropdownMenuLabel>
                {SPEEDS.map((s) => (
                  <DropdownMenuItem key={s} onClick={() => changeSpeed(s)} className="focus:bg-white/10">{speed === s && <Check className="w-4 h-4 mr-2" />} {s === 1 ? "Normal" : `${s}x`}</DropdownMenuItem>
                ))}
                {levels.length > 0 && <>
                  <DropdownMenuSeparator className="bg-white/10" />
                  <DropdownMenuLabel>Quality</DropdownMenuLabel>
                  <DropdownMenuItem onClick={() => changeLevel(-1)} className="focus:bg-white/10">{currentLevel === -1 && <Check className="w-4 h-4 mr-2" />} Auto</DropdownMenuItem>
                  {levels.map((l) => (
                    <DropdownMenuItem key={l.i} onClick={() => changeLevel(l.i)} className="focus:bg-white/10">{currentLevel === l.i && <Check className="w-4 h-4 mr-2" />} {l.name}</DropdownMenuItem>
                  ))}
                </>}
                {audioTracks.length > 1 && <>
                  <DropdownMenuSeparator className="bg-white/10" />
                  <DropdownMenuLabel>Audio</DropdownMenuLabel>
                  {audioTracks.map((a) => (
                    <DropdownMenuItem key={a.i} onClick={() => changeAudio(a.i)} className="focus:bg-white/10">{currentAudio === a.i && <Check className="w-4 h-4 mr-2" />} {a.name}</DropdownMenuItem>
                  ))}
                </>}
              </DropdownMenuContent>
            </DropdownMenu>
            <button data-testid="player-fullscreen" onClick={toggleFullscreen}><Maximize className="w-5 h-5" /></button>
          </div>
        </div>
      </div>
    </div>
  );
}
